import {
  createContext,
  forwardRef,
  useMemo,
  useImperativeHandle,
  useCallback,
  createRef,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import {
  useApolloClient,
  FetchResult,
  MutationFunctionOptions,
} from '@apollo/client';

import Loading from 'components/Loading';
import TeamChanger from 'components/TeamChanger';

import {
  isDealerTeam as isDealerTeamType,
  isFleet as isFleetType,
  isDealerGroup as isDealerGroupType,
  isSingleLocation as isSingleLocationType,
} from 'utils/SentinelTeam';
import { useCurrentUserContext } from 'utils/User';
import {
  buildTeamPath,
  getTeamKeyFromPath,
  stripTeamPrefix,
} from 'utils/teamFromUrl';
import ROUTE_NAMES from 'setup/Routing/routeNames';

import useCurrentSentinelTeamQuery from './useCurrentSentinelTeamQuery';
import useSetCurrentSentinelTeamMutation from './useSetCurrentSentinelTeamMutation';

type SetSentinelTeamMutationOpts = MutationFunctionOptions<
  SetCurrentSentinelTeamMutation,
  SetCurrentSentinelTeamMutationVariables
>;

type SetSentinelTeamType = (
  options?: SetSentinelTeamMutationOpts,
) => Promise<FetchResult<SetCurrentSentinelTeamMutation>> | Promise<void>;

interface SentinelTeamProviderProps {
  children: React.ReactNode;
}

interface SentinelTeamProviderHandles {
  setSentinelTeam: SetSentinelTeamType;
}

export interface SentinelTeamContextProps extends CurrentSentinelTeam {
  isFleet: boolean;
  isDealerTeam: boolean;
  isDealerGroup: boolean;
  isSingleLocation: boolean;
  isTeamMember: boolean;
  hasSentinelTeam: boolean;
  isTeamEnabled: boolean;
  belongsToOemSegment: boolean;
  setSentinelTeam: SetSentinelTeamType;
  logout: () => Promise<void>;
}

export const SentinelTeamContext = createContext<SentinelTeamContextProps>(
  {} as SentinelTeamContextProps,
);

const SentinelTeamProvider = forwardRef(
  (
    { children }: SentinelTeamProviderProps,
    ref: React.Ref<SentinelTeamProviderHandles>,
  ) => {
    const [isSettingTeam, setSettingTeam] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const client = useApolloClient();

    // Read teamKey from URL params (set by /t/:teamKey route)
    const { teamKey: urlTeamKey } = useParams<{ teamKey?: string }>();

    /* istanbul ignore next -- @preserve */
    const {
      sentinelTeams = [],
      availableTeamKeys = [],
      isSuperUser,
      isManager,
    } = useCurrentUserContext();

    const {
      data: {
        currentUserProfile: {
          user: { sentinelTeam = {} as CurrentSentinelTeam } = {},
        } = {},
      } = {},
      loading,
    } = useCurrentSentinelTeamQuery();

    const [updateSentinelTeam, { loading: setSentinelTeamLoading }] =
      useSetCurrentSentinelTeamMutation();

    const {
      teamType,
      id: teamId,
      enabled,
      teamKey: currentTeamKey,
    } = sentinelTeam || {};

    const isFleet = useMemo(() => isFleetType(teamType), [teamType]);
    const isDealerTeam = useMemo(
      () =>
        isDealerTeamType(teamType) ||
        isDealerGroupType(teamType) ||
        isSingleLocationType(teamType),
      [teamType],
    );

    const isDealerGroup = false;
    const isSingleLocation = false;
    const belongsToOemSegment = !!sentinelTeam?.oemSegment;

    const isTeamMember = useMemo(
      () => sentinelTeams.map(({ id }) => id).includes(teamId),
      [sentinelTeams, teamId],
    );

    const hasSentinelTeam = useMemo(() => !!teamId, [teamId]);

    const isTeamEnabled = useMemo(() => !!enabled, [enabled]);

    // Apollo cache reset when team changes in URL
    const prevTeamKeyRef = useRef(urlTeamKey);

    useEffect(() => {
      if (
        prevTeamKeyRef.current &&
        urlTeamKey &&
        prevTeamKeyRef.current !== urlTeamKey
      ) {
        client.resetStore().catch(() => {
          // ignore reset failures to avoid unhandled promise rejections
        });
      }
      prevTeamKeyRef.current = urlTeamKey;
    }, [urlTeamKey, client]);

    // Auto-redirect: bare URL → team-prefixed URL
    // If user visits a bare URL (no /t/:teamKey prefix) and the API returns a team,
    // redirect to /t/:teamKey/<current-path> to establish URL-based context.
    useEffect(() => {
      if (!loading && !urlTeamKey && currentTeamKey) {
        // Guard: if the URL already has a /t/ prefix (e.g. useParams couldn't
        // resolve teamKey because no <Route path="/t/:teamKey"> exists in the
        // tree, such as in tests), skip to avoid an infinite redirect loop.
        if (getTeamKeyFromPath(location.pathname)) return;

        // Don't prefix standalone pages that live outside team scope
        const teamlessRoutes = [
          ROUTE_NAMES.access_denied,
          ROUTE_NAMES.teams,
          ROUTE_NAMES.superadmin.oem_onboard,
          ROUTE_NAMES.superadmin.reports,
        ];
        if (
          location.pathname.startsWith(ROUTE_NAMES.admin.management) ||
          teamlessRoutes.includes(location.pathname)
        )
          return;

        const innerPath = `${location.pathname}${location.search}${location.hash}`;
        navigate(buildTeamPath(currentTeamKey, innerPath), { replace: true });
      }
    }, [
      loading,
      urlTeamKey,
      currentTeamKey,
      isSuperUser,
      isManager,
      location.pathname,
      location.search,
      location.hash,
      navigate,
    ]);

    // Key mismatch redirect: if URL team key doesn't match API-resolved team key,
    // either the user lacks access (→ access denied) or the key was renamed (→ redirect).
    useEffect(() => {
      if (
        !loading &&
        urlTeamKey &&
        currentTeamKey &&
        urlTeamKey !== currentTeamKey
      ) {
        const matchedTeam = availableTeamKeys.find(
          (mapping: { canonicalKey: string; usedTeamKeys?: string[] }) =>
            mapping.canonicalKey === urlTeamKey ||
            mapping.usedTeamKeys?.includes(urlTeamKey),
        );

        if (!matchedTeam) {
          navigate(ROUTE_NAMES.access_denied, {
            replace: true,
          });
          return;
        }
        const innerPath = stripTeamPrefix(
          `${location.pathname}${location.search}${location.hash}`,
        );
        navigate(buildTeamPath(matchedTeam.canonicalKey, innerPath), {
          replace: true,
        });
      }
    }, [
      loading,
      urlTeamKey,
      currentTeamKey,
      availableTeamKeys,
      sentinelTeams,
      location,
      navigate,
    ]);

    // Redirect to teamUrl if it's defined and host differs from current host.
    // Skip if only the scheme (http vs https) differs to avoid loops under forced HTTPS.
    // Skip entirely on Amplify PR preview domains (pattern: pr-<num>.<hash>.amplifyapp.com).
    useEffect(() => {
      const teamUrl = sentinelTeam?.teamUrl;

      if (teamUrl && !loading) {
        try {
          const url = new URL(teamUrl);
          const current = new URL(window.location.href);
          const amplifyPrPreview =
            /(^pr-\d+\.)[a-z0-9-]+\.amplifyapp\.com$/i.test(current.hostname);
          if (amplifyPrPreview) return;
          const targetHost = url.hostname.toLowerCase();
          const currentHost = current.hostname.toLowerCase();
          const schemeDiffOnly =
            targetHost === currentHost && url.protocol !== current.protocol;

          if (!schemeDiffOnly && targetHost !== currentHost) {
            const targetUrl = `${url.protocol}//${url.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.href = targetUrl;
          }
        } catch (error) {
          // Invalid URL, ignore and continue
        }
      }
    }, [sentinelTeam, loading]);

    // Team switching is now navigation-based: navigate to /t/:newTeamKey/
    // instead of a DB mutation + hard reload.
    /* istanbul ignore next -- @preserve */
    const setCurrentSentinelTeam = useCallback(
      async (options) => {
        // Extract teamKey from the options if available, otherwise fall back to
        // updating the DB (for cases like superadmin picking a team from the list).
        const teamKeyFromOpts = options?.variables?.teamKey as
          | string
          | undefined;

        if (teamKeyFromOpts) {
          // Navigation-based team switch — no DB mutation needed
          const innerPath = stripTeamPrefix(
            `${location.pathname}${location.search}${location.hash}`,
          );
          navigate(buildTeamPath(teamKeyFromOpts, innerPath || '/'));
          return;
        }

        // Fallback: use the mutation (e.g. for superadmin first-time team selection by ID)
        setSettingTeam(true);
        const result = await updateSentinelTeam(options);
        const newTeamKey =
          result?.data?.setCurrentSentinelTeam?.user?.sentinelTeam?.teamKey;
        if (newTeamKey) {
          navigate(buildTeamPath(newTeamKey, '/'));
          setSettingTeam(false);
        } else {
          // If somehow no teamKey came back, fall back to reload
          window.location.href = '/';
        }
      },
      [
        updateSentinelTeam,
        navigate,
        location.pathname,
        location.search,
        location.hash,
      ],
    );

    /* istanbul ignore next -- @preserve */
    const logout = useCallback(async () => {
      setSettingTeam(true);
      await updateSentinelTeam({
        variables: { id: null as unknown as any },
      });
      window.location.reload();
    }, [updateSentinelTeam]);

    /* istanbul ignore next -- @preserve */
    useImperativeHandle(
      ref,
      () => ({
        setSentinelTeam: setCurrentSentinelTeam,
      }),
      [setCurrentSentinelTeam],
    );

    const values = useMemo(
      () => ({
        ...sentinelTeam,
        isFleet,
        isDealerTeam,
        isDealerGroup,
        isSingleLocation,
        isTeamMember,
        belongsToOemSegment,
        hasSentinelTeam,
        isTeamEnabled,
        setSentinelTeam: setCurrentSentinelTeam,
        logout,
      }),
      [
        sentinelTeam,
        isFleet,
        isDealerTeam,
        isDealerGroup,
        isSingleLocation,
        isTeamMember,
        belongsToOemSegment,
        hasSentinelTeam,
        isTeamEnabled,
        setCurrentSentinelTeam,
        logout,
      ],
    );

    /* istanbul ignore next -- @preserve */
    if (setSentinelTeamLoading || isSettingTeam) {
      return <TeamChanger />;
    }

    if (loading) {
      return <Loading />;
    }

    return (
      <SentinelTeamContext.Provider value={values}>
        {children}
      </SentinelTeamContext.Provider>
    );
  },
);

SentinelTeamProvider.displayName = 'SentinelTeamProvider';

SentinelTeamProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const sentinelTeamProviderRef = createRef<SentinelTeamProviderHandles>();

/* istanbul ignore next -- @preserve */
export async function setSentinelTeam(opts?: SetSentinelTeamMutationOpts) {
  return sentinelTeamProviderRef?.current?.setSentinelTeam?.(opts);
}

export default SentinelTeamProvider;
